import React from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames/bind';

import Button from '@ott/button';

import _noop from 'lodash/noop';
import { l10n } from '@ott/l10n';

import TravellersCloserIcon from './icons/TravellersCloserIcon';

import s from './TravellersChildren.scss';

const cx = cls.bind(s);

class TravellersChildren extends React.PureComponent {
  static propTypes = {
    childrenAges: PropTypes.array,
    maxChildren: PropTypes.number.isRequired,
    childrenAgeRange: PropTypes.shape({
      start: PropTypes.number,
      end: PropTypes.number,
    }),
    onChildrenAgesChange: PropTypes.func,
    getAgeTitle: PropTypes.func,
    getAgesTitle: PropTypes.func,
    getSelectedAgeTitle: PropTypes.func,
    deviceInfo: PropTypes.shape({
      isTouchDevice: PropTypes.bool,
    }),
  };

  static defaultProps = {
    childrenAges: [],
    onChildrenAgesChange: _noop,
    childrenAgeRange: {
      start: 0,
      end: 18,
    },
    deviceInfo: {},
  };

  state = {
    agesListIsOpen: false,
  };

  // Хак для предотвращения срабатывания обработчика выбора элемента при прокрутке списка на touch-экране
  handleTouchMove = () => {
    this._isActive = false;
  };

  handleTouchStart = () => {
    this._isActive = true;
  };

  handleAddChild = () => {
    this.setState({ agesListIsOpen: true });
  };

  // setTimeout - xак для touch событий при всплытии.
  // Не сразу делать setState и перерендер, чтобы прошли проверки на findDomNode
  handleAddChildWrap = e => {
    setTimeout(this.handleAddChild.bind(this, e), 0);
  };

  handleAgeSelect = age => {
    const { childrenAges, onChildrenAgesChange } = this.props;

    onChildrenAgesChange([...childrenAges].concat(age));

    this.setState({ agesListIsOpen: false });
  };

  // setTimeout - xак для touch событий при всплытии. См. handleAddChildWrap
  handleTouchEndAgeSelect(value, e) {
    if (this._isActive) {
      setTimeout(this.handleAgeSelect.bind(this, value, e), 0);
    }
  }

  handleRemoveChild = i => {
    const { childrenAges, onChildrenAgesChange } = this.props;

    const newChildrenAges = [...childrenAges];
    newChildrenAges.splice(i, 1);
    onChildrenAgesChange(newChildrenAges);
  };

  // setTimeout - xак для touch событий при всплытии. См. handleAddChildWrap
  handleTouchEndRemoveChild = e => {
    setTimeout(this.handleRemoveChild.bind(this, e), 0);
  };

  renderChildrenList() {
    const {
      deviceInfo: { isTouchDevice },
      childrenAges,
    } = this.props;

    return (
      <div data-locator="list">
        {childrenAges.map((age, i) => {
          return (
            <div className={cx('child')} key={i} data-locator="child">
              {this.renderSelectedAgeText(age)}
              <div
                className={cx('remover')}
                onMouseUp={!isTouchDevice ? this.handleRemoveChild.bind(this, i) : undefined}
                onTouchEnd={
                  isTouchDevice ? this.handleTouchEndRemoveChild.bind(this, i) : undefined
                }
                data-locator={`remove-child remove-child-idx-${i}`}
              >
                <TravellersCloserIcon className={cx('icon')} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderSelectedAgeText(age) {
    const { getSelectedAgeTitle } = this.props;

    if (getSelectedAgeTitle) {
      return getSelectedAgeTitle(age);
    }

    return `${l10n('travellersControl.child')}, ${this.renderAgeText(age)}`;
  }

  renderAddChildNode() {
    const { agesListIsOpen } = this.state;

    return (
      <div
        className={cx('addChildBlock')}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
      >
        {agesListIsOpen ? (
          this.renderAgesList()
        ) : (
          <Button
            withFullWidth
            size="medium"
            purpose="neutral"
            specificLocators="add-child-trigger"
            onClick={this.handleAddChildWrap}
          >
            {l10n('travellersControl.addChild')}
          </Button>
        )}
      </div>
    );
  }

  renderAgesList() {
    const agesArr = [];
    const {
      childrenAgeRange,
      deviceInfo: { isTouchDevice },
    } = this.props;
    let currentAge = childrenAgeRange.start;

    while (currentAge < childrenAgeRange.end) {
      agesArr.push(
        <div
          className={cx('age', { 'age--isLast': currentAge === childrenAgeRange.end - 1 })}
          onMouseUp={!isTouchDevice ? this.handleAgeSelect.bind(this, currentAge) : undefined}
          onTouchEnd={
            isTouchDevice ? this.handleTouchEndAgeSelect.bind(this, currentAge) : undefined
          }
          key={currentAge}
          data-locator={`age-${currentAge}`}
        >
          {this.renderAgeText(currentAge)}
        </div>
      );

      currentAge++;
    }

    return (
      <div className={cx('ages')} data-locator="ages-container">
        <div className={cx('agesTitle')}>{this.renderAgesTitle()}</div>
        <div className={cx('agesList')}>{agesArr}</div>
      </div>
    );
  }

  renderAgeText(age) {
    const { getAgeTitle } = this.props;

    if (getAgeTitle) {
      return getAgeTitle(age);
    }

    return `${age} ${l10n('searchForm.agesChildren', { value: age })}`;
  }

  renderAgesTitle() {
    const { getAgesTitle } = this.props;

    if (getAgesTitle) {
      return getAgesTitle();
    }

    return l10n('travellersControl.chooseChildAge');
  }

  render() {
    const { childrenAges, maxChildren } = this.props;

    if (!maxChildren) {
      return null;
    }

    return (
      <div className={cx('TravellersChildren')} data-locator="travellers-children">
        {this.renderChildrenList()}
        {childrenAges.length < maxChildren && this.renderAddChildNode()}
      </div>
    );
  }
}

export default TravellersChildren;
